/** Uploaded file attached to a consultant person (CV, case study PDF/image, certificate). */
export const ProfileDocumentKind = {
  Cv: 'cv',
  CaseStudyFile: 'case_study_file',
  Certificate: 'certificate',
} as const;

export type ProfileDocumentKind =
  (typeof ProfileDocumentKind)[keyof typeof ProfileDocumentKind];

export const ProfileDocumentKindValues = Object.values(ProfileDocumentKind);

/** Moderation state for profile documents and text case studies. */
export const CredentialReviewStatus = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
} as const;

export type CredentialReviewStatus =
  (typeof CredentialReviewStatus)[keyof typeof CredentialReviewStatus];

export const CredentialReviewStatusValues = Object.values(CredentialReviewStatus);

/** Aggregated consultant credential trust state on the person profile. */
export const ConsultantCredentialsStatus = {
  None: 'none',
  PendingReview: 'pending_review',
  Verified: 'verified',
  Rejected: 'rejected',
} as const;

export type ConsultantCredentialsStatus =
  (typeof ConsultantCredentialsStatus)[keyof typeof ConsultantCredentialsStatus];

export const ConsultantCredentialsStatusValues = Object.values(
  ConsultantCredentialsStatus,
);

export interface ProfileDocumentDto {
  id: string;
  personId: string;
  kind: ProfileDocumentKind;
  originalName: string;
  mimeType: string;
  size: number;
  reviewStatus: CredentialReviewStatus;
  reviewedAt: string | null;
  reviewedByUserId: string | null;
  rejectionReason: string | null;
  sortOrder: number;
  replacesDocumentId: string | null;
  supersededAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReorderProfileDocumentsDto {
  documentIds: string[];
}

export interface AdminCredentialQueueItemAttachmentDto {
  id: string;
  originalName: string;
  mimeType: string;
  size: number;
}

export interface AdminCredentialQueueItemDto {
  type: 'document' | 'case_study';
  id: string;
  /** Person id for documents; null for case studies */
  personId: string | null;
  accountId: string | null;
  title: string;
  subtitle: string | null;
  createdAt: string;
  kind?: ProfileDocumentKind;
  /** Case-study attachments visible to admin/support during review. */
  attachments?: AdminCredentialQueueItemAttachmentDto[];
}

export interface ReviewCredentialDocumentDto {
  action: 'approve' | 'reject';
  rejectionReason?: string;
}

export interface ReviewCaseStudyDto {
  action: 'approve' | 'reject';
  rejectionReason?: string;
}

export interface SetConsultantCredentialsStatusDto {
  status: 'verified' | 'rejected';
  rejectionReason?: string;
}

export interface AdminPersonCredentialStatusDto {
  personId: string;
  firstName: string;
  lastName: string;
  accountId: string;
  accountName: string | null;
  credentialsStatus: ConsultantCredentialsStatus;
}
